import { getCostForEquipment } from '../data/work-costs-database';

interface DPEData {
  living_area: number;
  climate_zone: string;

  deperditions_baies_vitrees?: number;
  deperditions_murs?: number;
  deperditions_planchers_hauts?: number;
  deperditions_planchers_bas?: number;
  deperditions_toiture?: number;
  deperditions_ponts_thermiques?: number;
  deperditions_enveloppe?: number;
  deperditions_renouvellement_air?: number;

  besoin_chauffage?: number;
  besoin_ecs?: number;

  conso_chauffage_ef?: number;
  conso_ecs_ef?: number;
  conso_eclairage_ef?: number;
  conso_auxiliaires_ef?: number;

  heating_energy?: string;
  hot_water_energy?: string;

  qualite_isolation_murs?: string;
  qualite_isolation_menuiseries?: string;
  qualite_isolation_plancher_haut_comble_perdu?: string;
  qualite_isolation_plancher_bas?: string;

  primary_energy_consumption?: number;
  greenhouse_gas_emissions?: number;

  emission_ges_chauffage?: number;
  emission_ges_ecs?: number;
  emission_ges_eclairage?: number;
  emission_ges_auxiliaires?: number;
  emission_ges_5_usages?: number;
}

interface EquipmentStandard {
  id: string;
  category: string;
  name: string;
  description: string;
  u_value?: number;
  r_value?: number;
  efficiency?: number;
  cop?: number;
  energy_type?: string;
}

interface ImprovementCalculation {
  improvement_type: string;
  equipment_standard_id?: string;
  before_value: number;
  after_value: number;
  before_description: string;
  after_description: string;
  energy_savings: number;
  cost_savings: number;
  ges_reduction: number;
  estimated_cost: number;
  affected_surface?: number;
  affected_quantity?: number;
  insulation_coverage_percentage?: number;
  insulation_coverage_notes?: string;
  energy_savings_breakdown?: {
    insulation_savings?: number;
    thermal_bridges_reduction?: number;
    insulation_type?: 'ITE' | 'ITI';
    total?: number;
  };
  current_ges_total?: number;
  projected_ges_total?: number;
  is_gas_to_electric_replacement?: boolean;
}

/**
 * Coefficients de conversion pour l'énergie primaire (EP) selon le type d'énergie
 * Mis à jour selon la réglementation DPE 2026
 *
 * - Électricité : 1.9 (nouveau coefficient 2026, anciennement 2.3)
 * - Gaz, fioul, bois, etc. : 1.0
 *
 * Note : Ces coefficients s'appliquent à l'énergie finale (EF) pour obtenir l'énergie primaire (EP)
 * EP = EF × coefficient
 */
const PRIMARY_ENERGY_FACTORS_2026: Record<string, number> = {
  electricite: 1.9,
  gaz_naturel: 1.0,
  gaz_propane: 1.0,
  fioul: 1.0,
  bois: 1.0,
  chauffage_urbain: 1.0,
};

const ELECTRIC_RADIATOR_EFFICIENCY = 1.0;

const DEGREE_DAYS_BY_ZONE: Record<string, number> = {
  H1: 2800,
  H2: 2400,
  H3: 1800,
};

const ENERGY_PRICES: Record<string, number> = {
  electricite: 0.2276,
  gaz_naturel: 0.0880,
  gaz_propane: 0.1640,
  fioul: 0.1110,
  bois: 0.0620,
  chauffage_urbain: 0.0950,
};

const ENERGY_GES_FACTORS: Record<string, number> = {
  electricite: 0.079,
  gaz_naturel: 0.227,
  gaz_propane: 0.272,
  fioul: 0.324,
  bois: 0.030,
  chauffage_urbain: 0.100,
};

const ENERGY_GES_FACTORS_2026: Record<string, number> = {
  electricite: 0.065,
  gaz_naturel: 0.227,
  gaz_propane: 0.272,
  fioul: 0.324,
  bois: 0.030,
  chauffage_urbain: 0.100,
};

const FALLBACK_U_VALUES: Record<string, number> = {
  simple_vitrage: 5.0,
  double_vitrage_ancien: 2.8,
  double_vitrage_recent: 1.3,
  mur_non_isole: 1.5,
  mur_faiblement_isole: 0.8,
  mur_moyennement_isole: 0.4,
  toit_non_isole: 2.0,
  toit_faiblement_isole: 0.6,
  toit_moyennement_isole: 0.3,
};

function getDegreeDays(climateZone: string): number {
  return DEGREE_DAYS_BY_ZONE[climateZone] || DEGREE_DAYS_BY_ZONE.H2;
}

function getEnergyPrice(energyType: string): number {
  return ENERGY_PRICES[energyType] || ENERGY_PRICES.electricite;
}

function getGESFactor(energyType: string): number {
  return ENERGY_GES_FACTORS[energyType] || ENERGY_GES_FACTORS.electricite;
}

function getGESFactor2026(energyType: string): number {
  return ENERGY_GES_FACTORS_2026[energyType] || ENERGY_GES_FACTORS_2026.electricite;
}

function getPrimaryEnergyFactor2026(energyType: string): number {
  return PRIMARY_ENERGY_FACTORS_2026[energyType] || PRIMARY_ENERGY_FACTORS_2026.electricite;
}

function estimateCurrentUValue(quality: string | undefined, element: string): number {
  const qualityMap: Record<string, number> = {
    insuffisante: element === 'menuiseries' ? 2.8 : element === 'plancher' ? 1.2 : 1.5,
    moyenne: element === 'menuiseries' ? 1.8 : element === 'plancher' ? 0.7 : 0.8,
    bonne: element === 'menuiseries' ? 1.3 : element === 'plancher' ? 0.35 : 0.4,
    tres_bonne: element === 'menuiseries' ? 1.0 : element === 'plancher' ? 0.25 : 0.25,
  };

  return qualityMap[quality || 'moyenne'] || qualityMap.moyenne;
}

export function calculateWindowsImprovement(
  dpe: DPEData,
  newEquipment: EquipmentStandard
): ImprovementCalculation | null {
  const heatLoss = dpe.deperditions_baies_vitrees;
  if (!heatLoss || heatLoss === 0) {
    return null;
  }

  const currentUValue = estimateCurrentUValue(dpe.qualite_isolation_menuiseries, 'menuiseries');
  const newUValue = newEquipment.u_value || 1.3;

  if (newUValue >= currentUValue) {
    return null;
  }

  const improvementRatio = 1 - (newUValue / currentUValue);
  const degreeDays = getDegreeDays(dpe.climate_zone);

  const energySavings = (heatLoss * improvementRatio * degreeDays * 24) / 1000;

  const energyType = dpe.heating_energy || 'electricite';
  const energyPrice = getEnergyPrice(energyType);
  const costSavings = energySavings * energyPrice;

  const gesReduction = energySavings * getGESFactor2026(energyType);

  // Surface baies vitrées : données réelles ADEME, fallback 15% de la surface habitable
  const affectedSurface = (dpe as any).surface_baies_vitrees_total ?? (dpe.living_area * 0.15);
  const costData = getCostForEquipment(newEquipment.name, 'windows');
  let estimatedCost = 0;
  if (costData && costData.pricing_type === 'per_sqm') {
    estimatedCost = Math.round(affectedSurface * costData.price);
  }

  return {
    improvement_type: 'windows',
    equipment_standard_id: newEquipment.id,
    before_value: currentUValue,
    after_value: newUValue,
    before_description: `Fenêtres actuelles (Uw=${currentUValue.toFixed(1)} W/m².K)`,
    after_description: newEquipment.description || newEquipment.name,
    energy_savings: Math.round(energySavings),
    cost_savings: Math.round(costSavings),
    ges_reduction: Math.round(gesReduction),
    estimated_cost: estimatedCost,
    affected_surface: affectedSurface,
  };
}

export function calculateWallInsulationImprovement(
  dpe: DPEData,
  newEquipment: EquipmentStandard,
  userAnswers?: Record<string, any>
): ImprovementCalculation | null {
  const heatLoss = dpe.deperditions_murs;
  if (!heatLoss || heatLoss === 0) {
    return null;
  }

  const currentUValue = estimateCurrentUValue(dpe.qualite_isolation_murs, 'murs');
  const newUValue = newEquipment.u_value || 0.29;

  if (newUValue >= currentUValue) {
    return null;
  }

  const improvementRatio = 1 - (newUValue / currentUValue);
  const degreeDays = getDegreeDays(dpe.climate_zone);

  const energySavingsWalls = (heatLoss * improvementRatio * degreeDays * 24) / 1000;

  const equipmentExtended = newEquipment as EquipmentStandard & { insulation_location?: string };
  let insulationType: 'ITE' | 'ITI' = 'ITI';
  let thermalBridgesReductionCoeff = 0.25;

  if (equipmentExtended.insulation_location === 'exterior') {
    insulationType = 'ITE';
    thermalBridgesReductionCoeff = 0.75;
  } else if (equipmentExtended.insulation_location === 'interior') {
    insulationType = 'ITI';
    thermalBridgesReductionCoeff = 0.25;
  } else if (userAnswers) {
    const iteAuthorized = userAnswers['urbanisme_ite_6'];
    const itiFeasibility = userAnswers['faisabilite_iti_7'];

    if (iteAuthorized === 'authorized' || iteAuthorized === 'ite_autorisee') {
      insulationType = 'ITE';
      thermalBridgesReductionCoeff = 0.75;
    } else if (iteAuthorized === 'forbidden' || iteAuthorized === 'ite_interdite') {
      insulationType = 'ITI';
      thermalBridgesReductionCoeff = 0.25;
    } else if (itiFeasibility === 'difficult' || itiFeasibility === 'iti_difficile') {
      insulationType = 'ITE';
      thermalBridgesReductionCoeff = 0.75;
    }
  }

  const thermalBridgesHeatLoss = dpe.deperditions_ponts_thermiques || 0;
  const energySavingsThermalBridges =
    (thermalBridgesHeatLoss * thermalBridgesReductionCoeff * degreeDays * 24) / 1000;

  const totalEnergySavings = energySavingsWalls + energySavingsThermalBridges;

  let coveragePercentage = 100;
  let coverageNotes = '';

  if (userAnswers && userAnswers['pourcentage_murs_isoles_8']) {
    const percentageAnswer = userAnswers['pourcentage_murs_isoles_8'];

    if (percentageAnswer === 'custom') {
      coveragePercentage = parseFloat(userAnswers['pourcentage_murs_isoles_8_custom'] || '100');
    } else if (percentageAnswer === 'tous_murs') {
      coveragePercentage = 100;
    } else if (percentageAnswer === 'trois_facades') {
      coveragePercentage = 75;
    } else if (percentageAnswer === 'deux_facades') {
      coveragePercentage = 50;
    } else if (percentageAnswer === 'une_facade') {
      coveragePercentage = 25;
    } else {
      coveragePercentage = parseFloat(percentageAnswer) || 100;
    }

    coveragePercentage = Math.min(Math.max(coveragePercentage, 10), 100);

    if (userAnswers['pourcentage_murs_isoles_8_notes']) {
      coverageNotes = userAnswers['pourcentage_murs_isoles_8_notes'];
    }
  }

  const coverageCoefficient = coveragePercentage / 100;

  const adjustedEnergySavingsWalls = energySavingsWalls * coverageCoefficient;
  const adjustedEnergySavingsThermalBridges = energySavingsThermalBridges * coverageCoefficient;
  const adjustedTotalEnergySavings = adjustedEnergySavingsWalls + adjustedEnergySavingsThermalBridges;

  const energyType = dpe.heating_energy || 'electricite';
  const energyPrice = getEnergyPrice(energyType);
  const costSavings = adjustedTotalEnergySavings * energyPrice;

  const gesReduction = adjustedTotalEnergySavings * getGESFactor2026(energyType);

  // Surface murs : données réelles ADEME, fallback 1.5× la surface habitable
  const wallSurface = (dpe as any).surface_murs ?? (dpe.living_area * 1.5);
  const affectedSurface = wallSurface * coverageCoefficient;

  const costData = getCostForEquipment(newEquipment.name, 'wall_insulation');
  let estimatedCost = 0;
  if (costData && costData.pricing_type === 'per_sqm') {
    estimatedCost = Math.round(affectedSurface * costData.price);
  }

  let afterDescription = newEquipment.description || newEquipment.name;

  if (coveragePercentage < 100) {
    afterDescription = `Isolation ${coveragePercentage}% des murs (${Math.round(affectedSurface)} m² sur ${Math.round(wallSurface)} m²) | ${afterDescription}`;
  }

  if (insulationType === 'ITE' && adjustedEnergySavingsThermalBridges > 0) {
    afterDescription += ` | Bonus ITE : Traitement de ${Math.round(thermalBridgesReductionCoeff * 100)}% des ponts thermiques (+${Math.round(adjustedEnergySavingsThermalBridges)} kWh/an)`;
  } else if (adjustedEnergySavingsThermalBridges > 0) {
    afterDescription += ` | Amélioration partielle des ponts thermiques (+${Math.round(adjustedEnergySavingsThermalBridges)} kWh/an)`;
  }

  return {
    improvement_type: 'wall_insulation',
    equipment_standard_id: newEquipment.id,
    before_value: currentUValue,
    after_value: newUValue,
    before_description: `Isolation murs actuelle (U=${currentUValue.toFixed(2)} W/m².K)`,
    after_description: afterDescription,
    energy_savings: Math.round(adjustedTotalEnergySavings),
    cost_savings: Math.round(costSavings),
    ges_reduction: Math.round(gesReduction),
    estimated_cost: estimatedCost,
    affected_surface: affectedSurface,
    insulation_coverage_percentage: coveragePercentage,
    insulation_coverage_notes: coverageNotes || null,
    energy_savings_breakdown: {
      insulation_savings: Math.round(adjustedEnergySavingsWalls),
      thermal_bridges_reduction: Math.round(adjustedEnergySavingsThermalBridges),
      insulation_type: insulationType,
      total: Math.round(adjustedTotalEnergySavings),
    },
  };
}

export function calculateRoofInsulationImprovement(
  dpe: DPEData,
  newEquipment: EquipmentStandard
): ImprovementCalculation | null {
  const heatLoss = dpe.deperditions_planchers_hauts || dpe.deperditions_toiture;
  if (!heatLoss || heatLoss === 0) {
    return null;
  }

  const currentQuality = dpe.qualite_isolation_plancher_haut_comble_perdu;
  const currentUValue = estimateCurrentUValue(currentQuality, 'toit');
  const newUValue = newEquipment.u_value || 0.13;

  if (newUValue >= currentUValue) {
    return null;
  }

  const improvementRatio = 1 - (newUValue / currentUValue);
  const degreeDays = getDegreeDays(dpe.climate_zone);

  const energySavings = (heatLoss * improvementRatio * degreeDays * 24) / 1000;

  const energyType = dpe.heating_energy || 'electricite';
  const energyPrice = getEnergyPrice(energyType);
  const costSavings = energySavings * energyPrice;

  const gesReduction = energySavings * getGESFactor2026(energyType);

  // Surface planchers hauts : données réelles ADEME, fallback = surface habitable
  const roofSurface = (dpe as any).surface_planchers_hauts ?? dpe.living_area;

  const costData = getCostForEquipment(newEquipment.name, 'roof_insulation');
  let estimatedCost = 0;
  if (costData && costData.pricing_type === 'per_sqm') {
    estimatedCost = Math.round(roofSurface * costData.price);
  }

  return {
    improvement_type: 'roof_insulation',
    equipment_standard_id: newEquipment.id,
    before_value: currentUValue,
    after_value: newUValue,
    before_description: `Isolation combles actuelle (U=${currentUValue.toFixed(2)} W/m².K)`,
    after_description: newEquipment.description || newEquipment.name,
    energy_savings: Math.round(energySavings),
    cost_savings: Math.round(costSavings),
    ges_reduction: Math.round(gesReduction),
    estimated_cost: estimatedCost,
    affected_surface: roofSurface,
  };
}

function estimateGESByUsage(dpe: DPEData, use2026Factors: boolean = false): {
  chauffage: number;
  ecs: number;
  autres: number;
  total: number;
} {
  let chauffage = 0;
  let ecs = 0;
  let autres = 0;

  if (use2026Factors && dpe.conso_chauffage_ef && dpe.heating_energy) {
    const gesFactorChauffage = getGESFactor2026(dpe.heating_energy);
    chauffage = dpe.conso_chauffage_ef * gesFactorChauffage;
  } else if (dpe.emission_ges_chauffage !== undefined) {
    chauffage = dpe.emission_ges_chauffage;
  } else if (dpe.conso_chauffage_ef && dpe.heating_energy) {
    const gesFactorChauffage = getGESFactor(dpe.heating_energy);
    chauffage = dpe.conso_chauffage_ef * gesFactorChauffage;
  }

  if (use2026Factors && dpe.conso_ecs_ef && dpe.hot_water_energy) {
    const gesFactorECS = getGESFactor2026(dpe.hot_water_energy);
    ecs = dpe.conso_ecs_ef * gesFactorECS;
  } else if (dpe.emission_ges_ecs !== undefined) {
    ecs = dpe.emission_ges_ecs;
  } else if (dpe.conso_ecs_ef && dpe.hot_water_energy) {
    const gesFactorECS = getGESFactor(dpe.hot_water_energy);
    ecs = dpe.conso_ecs_ef * gesFactorECS;
  }

  if (dpe.emission_ges_eclairage !== undefined) {
    autres += dpe.emission_ges_eclairage;
  }
  if (dpe.emission_ges_auxiliaires !== undefined) {
    autres += dpe.emission_ges_auxiliaires;
  }

  if (autres === 0 && dpe.emission_ges_5_usages) {
    if (use2026Factors) {
      autres = dpe.emission_ges_5_usages - (chauffage > 0 ? chauffage : (dpe.emission_ges_chauffage || 0)) - (ecs > 0 ? ecs : (dpe.emission_ges_ecs || 0));
    } else {
      autres = dpe.emission_ges_5_usages - chauffage - ecs;
    }
    // "autres" by subtraction can go negative when 2026 factors differ from DPE original
    autres = Math.max(0, autres);
  }

  const total = chauffage + ecs + autres;

  return { chauffage, ecs, autres, total };
}

export function calculateTotalGESWithImprovements(
  dpe: DPEData,
  improvements: ImprovementCalculation[],
  livingArea: number
): {
  currentTotal: number;
  projectedTotal: number;
  totalReduction: number;
  projectedLabel: string;
} {
  const gesBreakdown = estimateGESByUsage(dpe, true);

  console.log('🌍 Calcul GES total - État actuel (avec facteurs 2026):', {
    chauffage: gesBreakdown.chauffage,
    ecs: gesBreakdown.ecs,
    autres: gesBreakdown.autres,
    total: gesBreakdown.total,
    surface: livingArea,
    parM2: gesBreakdown.total / livingArea,
    dpeEmissionGesOrigine: dpe.emission_ges_5_usages || dpe.greenhouse_gas_emissions,
    facteurElec2026: getGESFactor2026('electricite'),
    facteurGaz2026: getGESFactor2026('gaz_naturel')
  });

  const heatingReplacement = improvements.find(imp => imp.improvement_type === 'heating');
  const hotWaterReplacement = improvements.find(imp => imp.improvement_type === 'hot_water');

  const insulationImprovements = improvements.filter(imp =>
    imp.improvement_type !== 'heating' &&
    imp.improvement_type !== 'hot_water'
  );

  const totalInsulationEnergySavings = insulationImprovements.reduce(
    (sum, imp) => sum + (imp.energy_savings || 0),
    0
  );

  let newChauffage = gesBreakdown.chauffage;
  let newECS = gesBreakdown.ecs;

  if (heatingReplacement && heatingReplacement.projected_ges_total !== undefined) {
    if (totalInsulationEnergySavings > 0 && dpe.besoin_chauffage) {
      const currentHeatingNeed = dpe.besoin_chauffage;
      const reducedHeatingNeed = Math.max(0, currentHeatingNeed - totalInsulationEnergySavings);

      const newEnergyType = (heatingReplacement as any).energy_type ||
                            (improvements.find(imp => imp.improvement_type === 'heating')?.after_description?.toLowerCase().includes('pac') ? 'electricite' : 'gaz_naturel');

      let cop = 1;
      if ((heatingReplacement as any).cop) {
        cop = (heatingReplacement as any).cop;
      } else if (heatingReplacement.after_value > 100) {
        cop = heatingReplacement.after_value / 100;
      }

      const newConsumptionEF = reducedHeatingNeed / cop;
      newChauffage = newConsumptionEF * getGESFactor2026(newEnergyType);

      console.log('🔥 Remplacement chauffage avec isolations:', {
        besoinInitial: currentHeatingNeed,
        economiesIsolation: totalInsulationEnergySavings,
        besoinReduit: reducedHeatingNeed,
        cop: cop,
        consoEFnouvelle: newConsumptionEF,
        facteurGES2026: getGESFactor2026(newEnergyType),
        emissionsNouvelles: newChauffage,
        reduction: gesBreakdown.chauffage - newChauffage
      });
    } else {
      newChauffage = heatingReplacement.projected_ges_total;
      console.log('🔥 Remplacement chauffage seul:', {
        avant: gesBreakdown.chauffage,
        apres: newChauffage,
        reduction: gesBreakdown.chauffage - newChauffage
      });
    }
  } else if (totalInsulationEnergySavings > 0) {
    const currentEnergyType = dpe.heating_energy || 'electricite';
    const gesReductionFromInsulation = totalInsulationEnergySavings * getGESFactor2026(currentEnergyType);
    newChauffage = Math.max(0, gesBreakdown.chauffage - gesReductionFromInsulation);

    console.log('🏠 Isolations seules (sans remplacement):', {
      economiesEnergie: totalInsulationEnergySavings,
      facteurGES2026: getGESFactor2026(currentEnergyType),
      reductionGES: gesReductionFromInsulation,
      chauffageAvant: gesBreakdown.chauffage,
      chauffageApres: newChauffage
    });
  }

  if (hotWaterReplacement && hotWaterReplacement.projected_ges_total !== undefined) {
    newECS = hotWaterReplacement.projected_ges_total;
    console.log('💧 Remplacement ECS:', {
      avant: gesBreakdown.ecs,
      apres: newECS,
      reduction: gesBreakdown.ecs - newECS
    });
  }

  const projectedTotalKg = Math.max(0, newChauffage + newECS + gesBreakdown.autres);
  const currentTotalPerM2 = gesBreakdown.total / livingArea;
  const projectedTotalPerM2 = Math.max(0, projectedTotalKg / livingArea);
  const totalReduction = currentTotalPerM2 - projectedTotalPerM2;

  console.log('🌍 Calcul GES total - État projeté:', {
    chauffage: newChauffage,
    ecs: newECS,
    autres: gesBreakdown.autres,
    total: projectedTotalKg,
    surface: livingArea,
    parM2: projectedTotalPerM2,
    reductionParM2: totalReduction,
    reductionTotale: gesBreakdown.total - projectedTotalKg,
    labelActuel: getGESLabel(currentTotalPerM2),
    labelProjet: getGESLabel(projectedTotalPerM2)
  });

  const projectedLabel = getGESLabel(projectedTotalPerM2);

  return {
    currentTotal: currentTotalPerM2,
    projectedTotal: projectedTotalPerM2,
    totalReduction,
    projectedLabel
  };
}

function getGESLabel(gesPerM2: number): string {
  if (!isFinite(gesPerM2) || gesPerM2 < 0) return 'A';
  if (gesPerM2 <= 6) return 'A';
  if (gesPerM2 <= 11) return 'B';
  if (gesPerM2 <= 30) return 'C';
  if (gesPerM2 <= 50) return 'D';
  if (gesPerM2 <= 70) return 'E';
  if (gesPerM2 <= 100) return 'F';
  return 'G';
}

/**
 * Détecte si on remplace un chauffage non-électrique (gaz, fioul, bois)
 * par des radiateurs électriques (pas une PAC)
 *
 * @param currentEnergyType - Type d'énergie actuel (ex: 'gaz_naturel', 'fioul')
 * @param newEquipmentName - Nom du nouvel équipement
 * @param cop - COP du nouvel équipement (undefined ou ~1.0 pour radiateurs électriques)
 * @returns true si c'est un remplacement gaz/fioul/bois → radiateurs électriques
 */
function isGasToElectricRadiatorsReplacement(
  currentEnergyType: string,
  newEquipmentName: string,
  cop: number | null
): boolean {
  const isCurrentNonElectric =
    currentEnergyType.includes('gaz') ||
    currentEnergyType.includes('fioul') ||
    currentEnergyType.includes('bois');

  const isElectricRadiator =
    newEquipmentName.toLowerCase().includes('radiateur') ||
    newEquipmentName.toLowerCase().includes('convecteur') ||
    newEquipmentName.toLowerCase().includes('panneau rayonnant');

  const isNotHeatPump = !cop || cop <= 1.1;

  return isCurrentNonElectric && isElectricRadiator && isNotHeatPump;
}

export function calculateHeatingSystemImprovement(
  dpe: DPEData,
  newEquipment: EquipmentStandard
): ImprovementCalculation | null {
  const heatingNeed = dpe.besoin_chauffage;
  if (!heatingNeed || heatingNeed === 0) {
    return null;
  }

  const currentEnergyType = dpe.heating_energy || 'electricite';
  const newEnergyType = newEquipment.energy_type || 'gaz_naturel';

  const currentConsumptionEF = dpe.conso_chauffage_ef || heatingNeed;
  const currentEfficiency = (heatingNeed / currentConsumptionEF) * 100;

  let newConsumptionEF: number;
  let newEfficiencyDisplay: number;
  let actualCOP: number | null = null;

  if (newEquipment.cop && newEquipment.cop > 1) {
    actualCOP = newEquipment.cop;
  } else if (newEquipment.efficiency && newEquipment.efficiency > 100) {
    actualCOP = newEquipment.efficiency / 100;
  }

  if (actualCOP) {
    newConsumptionEF = heatingNeed / actualCOP;
    newEfficiencyDisplay = actualCOP * 100;
  } else {
    const newEfficiency = newEquipment.efficiency || 95;
    newConsumptionEF = heatingNeed / (newEfficiency / 100);
    newEfficiencyDisplay = newEfficiency;
  }

  // ✅ CORRECTION : Le calcul de newConsumptionEF est déjà correct ci-dessus
  // Il utilise le besoin de chauffage et le rendement/COP du nouvel équipement
  // Pour un remplacement gaz → radiateurs électriques :
  // - Gaz actuel : besoin_chauffage / rendement_gaz (ex: 7596 / 0.70 = 10851 kWh EF)
  // - Électrique : besoin_chauffage / 1.0 (ex: 7596 / 1.0 = 7596 kWh EF)
  // → Économie d'énergie finale (EF) : 10851 - 7596 = 3255 kWh/an
  //
  // MAIS en énergie primaire (EP) :
  // - Gaz actuel : 10851 × 1.0 = 10851 kWh EP
  // - Électrique : 7596 × 1.9 = 14433 kWh EP
  // → Augmentation d'énergie primaire (EP) : +3582 kWh EP
  //
  // C'est cette distinction EF vs EP qui est importante et doit être gérée dans
  // le calcul du DPE projeté, pas ici.

  let energySavingsEF = currentConsumptionEF - newConsumptionEF;
  let isGasToElectricReplacement = false;

  // Détecter si c'est un remplacement gaz/fioul/bois → radiateurs électriques
  if (isGasToElectricRadiatorsReplacement(currentEnergyType, newEquipment.name, actualCOP)) {
    isGasToElectricReplacement = true;

    console.log('⚡ Détection remplacement combustible → radiateurs électriques:', {
      energieActuelle: currentEnergyType,
      nouvelEquipement: newEquipment.name,
      besoinChauffage: heatingNeed,
      consoEFactuelle: currentConsumptionEF,
      consoEFnouvelle: newConsumptionEF,
      economiesEF: energySavingsEF,
      noteImportante: 'Économie en EF, mais augmentation en EP à cause du coefficient 1.9'
    });
  }

  // Ne pas rejeter les remplacements qui ont des économies EF négatives si c'est un remplacement vers l'électrique
  // car l'énergie primaire sera calculée séparément
  if (energySavingsEF <= 0 && !isGasToElectricReplacement) {
    return null;
  }

  const currentCost = currentConsumptionEF * getEnergyPrice(currentEnergyType);
  const newCost = newConsumptionEF * getEnergyPrice(newEnergyType);
  const costSavings = currentCost - newCost;

  // ✅ CORRECTION : Toujours recalculer avec les facteurs 2026 pour être cohérent
  // avec le calcul global dans estimateGESByUsage(dpe, true)
  const currentGESTotal = currentConsumptionEF * getGESFactor2026(currentEnergyType);
  const projectedGESTotal = newConsumptionEF * getGESFactor2026(newEnergyType);
  const gesReduction = currentGESTotal - projectedGESTotal;

  console.log('🔥 Calcul émissions chauffage:', {
    energieActuelle: currentEnergyType,
    nouvelleEnergie: newEnergyType,
    copDetecte: actualCOP,
    consoEFactuelle: currentConsumptionEF,
    consoEFnouvelle: newConsumptionEF,
    facteurGESactuel2026: getGESFactor2026(currentEnergyType),
    facteurGESnouveau2026: getGESFactor2026(newEnergyType),
    emissionsActuelles: currentGESTotal,
    emissionsNouvelles: projectedGESTotal,
    reductionGES: gesReduction,
    dpeEmissionGesChauffage: dpe.emission_ges_chauffage,
    differenceAvecDPE: dpe.emission_ges_chauffage ? currentGESTotal - dpe.emission_ges_chauffage : 'N/A',
    isGasToElectricReplacement
  });

  const costData = getCostForEquipment(newEquipment.name, 'heating');
  let estimatedCost = 0;
  if (costData && costData.pricing_type === 'fixed') {
    estimatedCost = costData.price;
  }

  const result: ImprovementCalculation & { energy_type?: string; cop?: number } = {
    improvement_type: 'heating',
    equipment_standard_id: newEquipment.id,
    before_value: currentEfficiency,
    after_value: newEfficiencyDisplay,
    before_description: `Système actuel (rendement ${currentEfficiency.toFixed(0)}%)`,
    after_description: newEquipment.description || newEquipment.name,
    energy_savings: Math.round(energySavingsEF),
    cost_savings: Math.round(costSavings),
    ges_reduction: Math.round(gesReduction),
    estimated_cost: estimatedCost,
    current_ges_total: currentGESTotal,
    projected_ges_total: projectedGESTotal,
    energy_type: newEnergyType,
    cop: actualCOP || undefined,
    is_gas_to_electric_replacement: isGasToElectricReplacement,
  };

  return result;
}

function getOptimalVolumeForOccupants(nbHabitants: number): number {
  if (nbHabitants <= 1) return 65;
  if (nbHabitants === 2) return 80;
  if (nbHabitants === 3) return 100;
  return 150;
}

function adjustEfficiencyForVolume(
  baseEfficiency: number,
  volumeEquipment: number,
  volumeOptimal: number
): number {
  if (volumeEquipment <= volumeOptimal) return baseEfficiency;
  const penalty = volumeOptimal / volumeEquipment;
  return Math.max(baseEfficiency * penalty, baseEfficiency * 0.6);
}

function extractVolumeFromEquipment(equipment: EquipmentStandard): number | null {
  if (equipment.volume_liters && equipment.volume_liters > 0) {
    return equipment.volume_liters;
  }
  const match = equipment.name.match(/(\d+)\s*l/i);
  if (match) {
    const vol = parseInt(match[1], 10);
    if (vol >= 10 && vol <= 1000) return vol;
  }
  return null;
}

export function calculateHotWaterSystemImprovement(
  dpe: DPEData,
  newEquipment: EquipmentStandard,
  userAnswers?: Record<string, any>
): ImprovementCalculation | null {
  const ecsNeed = dpe.besoin_ecs;
  if (!ecsNeed || ecsNeed === 0) {
    return null;
  }

  const currentConsumption = dpe.conso_ecs_ef || ecsNeed;
  const currentEfficiency = (ecsNeed / currentConsumption) * 100;

  const baseEfficiency = newEquipment.efficiency || 90;

  const nbHabitants = userAnswers?.nombre_habitants
    ? parseInt(String(userAnswers.nombre_habitants), 10) || 2
    : 2;
  const volumeOptimal = getOptimalVolumeForOccupants(nbHabitants);
  const volumeEquipment = extractVolumeFromEquipment(newEquipment);

  const newEfficiency = volumeEquipment
    ? adjustEfficiencyForVolume(baseEfficiency, volumeEquipment, volumeOptimal)
    : baseEfficiency;

  if (newEfficiency <= currentEfficiency) {
    return null;
  }

  const newConsumption = ecsNeed / (newEfficiency / 100);
  const energySavings = currentConsumption - newConsumption;

  const currentEnergyType = dpe.hot_water_energy || 'electricite';
  const newEnergyType = newEquipment.energy_type || 'electricite';

  const currentCost = currentConsumption * getEnergyPrice(currentEnergyType);
  const newCost = newConsumption * getEnergyPrice(newEnergyType);
  const costSavings = currentCost - newCost;

  const currentGESTotal = currentConsumption * getGESFactor2026(currentEnergyType);
  const projectedGESTotal = newConsumption * getGESFactor2026(newEnergyType);
  const gesReduction = currentGESTotal - projectedGESTotal;

  const costData = getCostForEquipment(newEquipment.name, 'hot_water');
  let estimatedCost = 0;
  if (costData && costData.pricing_type === 'fixed') {
    estimatedCost = costData.price;
  }

  return {
    improvement_type: 'hot_water',
    equipment_standard_id: newEquipment.id,
    before_value: currentEfficiency,
    after_value: newEfficiency,
    before_description: `Système actuel (rendement ${currentEfficiency.toFixed(0)}%)`,
    after_description: newEquipment.description || newEquipment.name,
    energy_savings: Math.round(energySavings),
    cost_savings: Math.round(costSavings),
    ges_reduction: Math.round(gesReduction),
    estimated_cost: estimatedCost,
    current_ges_total: currentGESTotal,
    projected_ges_total: projectedGESTotal,
  };
}

export function calculateVentilationImprovement(
  dpe: DPEData,
  newEquipment: EquipmentStandard
): ImprovementCalculation | null {
  let ventilationLoss = dpe.deperditions_renouvellement_air;

  if (!ventilationLoss || ventilationLoss === 0) {
    const livingArea = dpe.living_area || 70;
    const volume = livingArea * 2.5;
    const airRenewalRate = 0.8;
    const airHeatCapacity = 0.34;
    const energyClassCoeff = dpe.primary_energy_consumption ? (dpe.primary_energy_consumption / 250) : 1.0;
    const climateFactor = dpe.climate_zone === 'H1' ? 1.2 : dpe.climate_zone === 'H3' ? 0.8 : 1.0;

    ventilationLoss = Math.round(volume * airRenewalRate * airHeatCapacity * energyClassCoeff * climateFactor);
  }

  if (!ventilationLoss || ventilationLoss === 0) {
    return null;
  }

  const currentEfficiency = 0;
  const newEfficiency = newEquipment.efficiency || 70;

  const improvementRatio = (newEfficiency / 100) * 0.3;
  const degreeDays = getDegreeDays(dpe.climate_zone);

  const energySavings = (ventilationLoss * improvementRatio * degreeDays * 24) / 1000;

  const energyType = dpe.heating_energy || 'electricite';
  const energyPrice = getEnergyPrice(energyType);
  const costSavings = energySavings * energyPrice;

  const gesReduction = energySavings * getGESFactor2026(energyType);

  const costData = getCostForEquipment(newEquipment.name, 'ventilation');
  let estimatedCost = 0;
  if (costData && costData.pricing_type === 'fixed') {
    estimatedCost = costData.price;
  }

  return {
    improvement_type: 'ventilation',
    equipment_standard_id: newEquipment.id,
    before_value: currentEfficiency,
    after_value: newEfficiency,
    before_description: 'Ventilation naturelle ou VMC défaillante',
    after_description: newEquipment.description || newEquipment.name,
    energy_savings: Math.round(energySavings),
    cost_savings: Math.round(costSavings),
    ges_reduction: Math.round(gesReduction),
    estimated_cost: estimatedCost,
  };
}

export function calculateFloorInsulationImprovement(
  dpe: DPEData,
  newEquipment: EquipmentStandard
): ImprovementCalculation | null {
  const heatLoss = dpe.deperditions_planchers_bas;
  if (!heatLoss || heatLoss === 0) {
    return null;
  }

  const currentUValue = estimateCurrentUValue(dpe.qualite_isolation_plancher_bas, 'plancher');
  const newRValue = newEquipment.r_value || 3.5;
  const newUValue = 1 / newRValue;

  if (newUValue >= currentUValue) {
    return null;
  }

  const improvementRatio = 1 - (newUValue / currentUValue);
  const degreeDays = getDegreeDays(dpe.climate_zone);

  const energySavings = ((heatLoss * improvementRatio * degreeDays * 24) / 1000) * 0.6;

  const energyType = dpe.heating_energy || 'electricite';
  const energyPrice = getEnergyPrice(energyType);
  const costSavings = energySavings * energyPrice;

  const gesReduction = energySavings * getGESFactor2026(energyType);

  // Surface planchers bas : données réelles ADEME, fallback = surface habitable
  const floorSurface = (dpe as any).surface_planchers_bas ?? dpe.living_area;

  const costData = getCostForEquipment(newEquipment.name, 'floor_insulation');
  let estimatedCost = 0;
  if (costData && costData.pricing_type === 'per_sqm') {
    estimatedCost = Math.round(floorSurface * costData.price);
  }

  return {
    improvement_type: 'floor_insulation',
    equipment_standard_id: newEquipment.id,
    before_value: currentUValue,
    after_value: newUValue,
    before_description: `Isolation plancher actuelle (U=${currentUValue.toFixed(2)} W/m².K)`,
    after_description: newEquipment.description || newEquipment.name,
    energy_savings: Math.round(energySavings),
    cost_savings: Math.round(costSavings),
    ges_reduction: Math.round(gesReduction),
    estimated_cost: estimatedCost,
    affected_surface: floorSurface,
  };
}

export function calculateHeatingControlImprovement(
  dpe: DPEData,
  newEquipment: EquipmentStandard,
  userAnswers?: Record<string, any>
): ImprovementCalculation | null {
  const heatingConsumption = dpe.conso_chauffage_ef;
  if (!heatingConsumption || heatingConsumption === 0) {
    return null;
  }

  const regulationAnswer = userAnswers?.regulation_programmable;

  if (regulationAnswer === 'regulation_equipped') {
    return null;
  }

  if (regulationAnswer && regulationAnswer !== 'regulation_not_equipped' && regulationAnswer !== 'regulation_partial') {
  return null;
}

  const savingsRate = 0.06;
  const energySavings = heatingConsumption * savingsRate;

  const energyType = dpe.heating_energy || 'electricite';
  const energyPrice = getEnergyPrice(energyType);
  const costSavings = energySavings * energyPrice;

  const gesReduction = energySavings * getGESFactor2026(energyType);

  const costData = getCostForEquipment(newEquipment.name, 'heating_control');
  let estimatedCost = 0;
  if (costData && costData.pricing_type === 'fixed') {
    estimatedCost = costData.price;
  }

  return {
    improvement_type: 'heating_control',
    equipment_standard_id: newEquipment.id,
    before_value: 0,
    after_value: 100,
    before_description: regulationAnswer === 'regulation_partial'
      ? 'Régulation partielle ou insuffisante'
      : 'Absence de régulation programmable',
    after_description: newEquipment.description || `${newEquipment.name} — gain 6% sur le chauffage (méthode DPE)`,
    energy_savings: Math.round(energySavings),
    cost_savings: Math.round(costSavings),
    ges_reduction: Math.round(gesReduction),
    estimated_cost: estimatedCost,
  };
}

export function calculateScenarioTotals(improvements: ImprovementCalculation[]): {
  totalEnergySavings: number;
  totalCostSavings: number;
  totalInvestment: number;
  totalGESReduction: number;
  paybackPeriod: number;
} {
  const totalEnergySavings = improvements.reduce((sum, imp) => sum + imp.energy_savings, 0);
  const totalCostSavings = improvements.reduce((sum, imp) => sum + imp.cost_savings, 0);
  const totalInvestment = improvements.reduce((sum, imp) => sum + imp.estimated_cost, 0);
  const totalGESReduction = improvements.reduce((sum, imp) => sum + imp.ges_reduction, 0);

  const paybackPeriod = totalCostSavings > 0 ? totalInvestment / totalCostSavings : 999;

  return {
    totalEnergySavings: Math.round(totalEnergySavings),
    totalCostSavings: Math.round(totalCostSavings),
    totalInvestment: Math.round(totalInvestment),
    totalGESReduction: Math.round(totalGESReduction),
    paybackPeriod: Math.round(paybackPeriod * 10) / 10,
  };
}

/**
 * Calcule l'énergie primaire projetée en tenant compte des changements d'énergie
 * et des nouveaux coefficients 2026
 *
 * Cette fonction gère correctement le cas où on change de type d'énergie
 * (ex: gaz → électricité), ce qui impacte le calcul de l'EP
 */
export function calculateProjectedPrimaryEnergy(
  dpe: DPEData,
  improvements: ImprovementCalculation[],
  livingArea: number
): {
  currentEP: number;
  projectedEP: number;
  currentEPperM2: number;
  projectedEPperM2: number;
} {
  // Récupérer les consommations EF actuelles par usage
  const currentHeatingEF = dpe.conso_chauffage_ef || 0;
  const currentHotWaterEF = dpe.conso_ecs_ef || 0;
  const currentLightingEF = dpe.conso_eclairage_ef || 0;
  const currentAuxiliariesEF = dpe.conso_auxiliaires_ef || 0;

  // Calculer l'EP actuelle avec les coefficients 2026
  const currentHeatingEP = currentHeatingEF * getPrimaryEnergyFactor2026(dpe.heating_energy || 'electricite');
  const currentHotWaterEP = currentHotWaterEF * getPrimaryEnergyFactor2026(dpe.hot_water_energy || 'electricite');
  const currentLightingEP = currentLightingEF * getPrimaryEnergyFactor2026('electricite');
  const currentAuxiliariesEP = currentAuxiliariesEF * getPrimaryEnergyFactor2026('electricite');

  const currentTotalEP = currentHeatingEP + currentHotWaterEP + currentLightingEP + currentAuxiliariesEP;

  // Identifier les remplacements d'équipements qui changent le type d'énergie
  const heatingReplacement = improvements.find(imp => imp.improvement_type === 'heating');
  const hotWaterReplacement = improvements.find(imp => imp.improvement_type === 'hot_water');

  // Calculer les consommations EF après améliorations
  let projectedHeatingEF = currentHeatingEF;
  let projectedHeatingEnergyType = dpe.heating_energy || 'electricite';

  // Identifier les améliorations d'isolation qui réduisent le besoin de chauffage
  const insulationImprovements = improvements.filter(imp =>
    imp.improvement_type === 'windows' ||
    imp.improvement_type === 'wall_insulation' ||
    imp.improvement_type === 'roof_insulation' ||
    imp.improvement_type === 'floor_insulation' ||
    imp.improvement_type === 'ventilation' ||
    imp.improvement_type === 'heating_control'
  );
  const totalInsulationSavings = insulationImprovements.reduce((sum, imp) => sum + (imp.energy_savings || 0), 0);

  if (heatingReplacement) {
    // Si on remplace le chauffage ET qu'il y a des isolations:
    // 1. D'abord réduire le besoin de chauffage avec les isolations
    // 2. Puis calculer la consommation EF avec le nouveau système sur le besoin réduit
    let heatingNeed = dpe.besoin_chauffage || 0;

    // Appliquer les économies d'isolation au besoin de chauffage
    if (totalInsulationSavings > 0) {
      heatingNeed = Math.max(0, heatingNeed - totalInsulationSavings);
      console.log('🏠 Réduction du besoin de chauffage avec isolations:', {
        besoinInitial: dpe.besoin_chauffage,
        economiesIsolation: totalInsulationSavings,
        besoinReduit: heatingNeed
      });
    }

    const heatingReplacementAny = heatingReplacement as any;
    const newEnergyType = heatingReplacementAny.energy_type ||
                          (heatingReplacement.energy_savings_breakdown as any)?.energy_type ||
                          'electricite';
    const cop = heatingReplacementAny.cop ||
                (heatingReplacement.energy_savings_breakdown as any)?.cop ||
                (heatingReplacement.after_value > 100 ? heatingReplacement.after_value / 100 : 1.0);

    projectedHeatingEF = heatingNeed / cop;
    projectedHeatingEnergyType = newEnergyType;

    console.log('🔄 Remplacement chauffage détecté pour calcul EP:', {
      besoinChauffage: heatingNeed,
      nouvelleEnergie: newEnergyType,
      cop: cop,
      consoEFnouvelle: projectedHeatingEF,
      ancienneEnergie: dpe.heating_energy,
      consoEFancienne: currentHeatingEF
    });
  } else {
    // Pas de remplacement de chauffage: appliquer les économies des isolations directement
    projectedHeatingEF = Math.max(0, currentHeatingEF - totalInsulationSavings);
  }

  let projectedHotWaterEF = currentHotWaterEF;
  let projectedHotWaterEnergyType = dpe.hot_water_energy || 'electricite';

  if (hotWaterReplacement) {
    const ecsNeed = dpe.besoin_ecs || 0;
    const newEnergyType = (hotWaterReplacement.energy_savings_breakdown as any)?.energy_type || 'electricite';
    const efficiency = hotWaterReplacement.after_value / 100 || 1.0;

    projectedHotWaterEF = ecsNeed / efficiency;
    projectedHotWaterEnergyType = newEnergyType;
  }

  // Calculer l'EP projetée avec les bons coefficients selon le type d'énergie
  const projectedHeatingEP = projectedHeatingEF * getPrimaryEnergyFactor2026(projectedHeatingEnergyType);
  const projectedHotWaterEP = projectedHotWaterEF * getPrimaryEnergyFactor2026(projectedHotWaterEnergyType);
  const projectedLightingEP = currentLightingEP; // Pas de changement
  const projectedAuxiliariesEP = currentAuxiliariesEP; // Pas de changement

  const projectedTotalEP = projectedHeatingEP + projectedHotWaterEP + projectedLightingEP + projectedAuxiliariesEP;

  console.log('📊 Calcul énergie primaire (EP) avec coefficient 2026:', {
    currentEP: {
      chauffage: currentHeatingEP,
      ecs: currentHotWaterEP,
      eclairage: currentLightingEP,
      auxiliaires: currentAuxiliariesEP,
      total: currentTotalEP,
      perM2: currentTotalEP / livingArea
    },
    projectedEP: {
      chauffage: projectedHeatingEP,
      ecs: projectedHotWaterEP,
      eclairage: projectedLightingEP,
      auxiliaires: projectedAuxiliariesEP,
      total: projectedTotalEP,
      perM2: projectedTotalEP / livingArea
    },
    changements: {
      chauffageEF: `${currentHeatingEF} → ${projectedHeatingEF} kWh/an`,
      chauffageEnergie: `${dpe.heating_energy} → ${projectedHeatingEnergyType}`,
      ecsEF: `${currentHotWaterEF} → ${projectedHotWaterEF} kWh/an`,
      ecsEnergie: `${dpe.hot_water_energy} → ${projectedHotWaterEnergyType}`
    }
  });

  return {
    currentEP: currentTotalEP,
    projectedEP: projectedTotalEP,
    currentEPperM2: currentTotalEP / livingArea,
    projectedEPperM2: projectedTotalEP / livingArea,
  };
}

/**
 * Calcule la classe DPE projetée à partir de la consommation en énergie primaire (EP)
 *
 * @param projectedEPperM2 - Consommation d'énergie primaire projetée en kWh/m²/an
 * @param livingArea - Surface habitable en m²
 */
export function calculateProjectedDPELabelFromEP(
  projectedEPperM2: number,
  livingArea: number
): {
  projectedConsumption: number;
  projectedLabel: string;
} {
  // Utiliser les seuils adaptatifs de la réforme juillet 2024 pour petites surfaces
  const thresholds = livingArea ? getDPEThresholds(livingArea) : [70, 110, 180, 250, 330, 420];

  let label = 'A';
  if (projectedEPperM2 > thresholds[0]) label = 'B';
  if (projectedEPperM2 > thresholds[1]) label = 'C';
  if (projectedEPperM2 > thresholds[2]) label = 'D';
  if (projectedEPperM2 > thresholds[3]) label = 'E';
  if (projectedEPperM2 > thresholds[4]) label = 'F';
  if (projectedEPperM2 > thresholds[5]) label = 'G';

  console.log('🏷️ Calcul classe DPE depuis EP:', {
    epPerM2: projectedEPperM2,
    surface: livingArea,
    seuils: thresholds,
    classeResultante: label
  });

  return {
    projectedConsumption: Math.round(projectedEPperM2),
    projectedLabel: label,
  };
}

/**
 * @deprecated Utiliser calculateProjectedDPELabelFromEP avec calculateProjectedPrimaryEnergy
 * Cette fonction est conservée pour compatibilité mais ne gère pas correctement
 * les changements de type d'énergie
 */
export function calculateProjectedDPELabel(
  currentConsumption: number,
  energySavings: number,
  livingArea: number
): {
  projectedConsumption: number;
  projectedLabel: string;
} {
  // ✅ CORRECTION : currentConsumption est déjà en kWh/m²/an
  // energySavings est en kWh/an total, donc on divise par la surface
  const energySavingsPerM2 = energySavings / livingArea;
  const projectedConsumption = Math.max(0, currentConsumption - energySavingsPerM2);

  console.log('🔧 DPE Label Calculation (DEPRECATED - ne gère pas les changements d\'énergie):', {
    currentConsumption,
    energySavingsTotal: energySavings,
    energySavingsPerM2,
    projectedConsumption,
    livingArea
  });

  // Utiliser les seuils adaptatifs de la réforme juillet 2024 pour petites surfaces
  const thresholds = livingArea ? getDPEThresholds(livingArea) : [70, 110, 180, 250, 330, 420];

  let label = 'A';
  if (projectedConsumption > thresholds[0]) label = 'B';
  if (projectedConsumption > thresholds[1]) label = 'C';
  if (projectedConsumption > thresholds[2]) label = 'D';
  if (projectedConsumption > thresholds[3]) label = 'E';
  if (projectedConsumption > thresholds[4]) label = 'F';
  if (projectedConsumption > thresholds[5]) label = 'G';

  return {
    projectedConsumption: Math.round(projectedConsumption),
    projectedLabel: label,
  };
}

/**
 * Retourne les seuils DPE adaptés selon la surface (réforme juillet 2024)
 * Les petites surfaces (<40m²) bénéficient de seuils plus élevés
 */
function getDPEThresholds(surface: number): number[] {
  if (surface <= 10) {
    return [123, 163, 328, 427, 532, 639];
  } else if (surface <= 20) {
    return [99, 139, 262, 332, 420, 513];
  } else if (surface <= 25) {
    return [87, 127, 229, 299, 384, 475];
  } else if (surface <= 30) {
    return [80, 120, 209, 279, 362, 453];
  } else if (surface <= 35) {
    return [75, 115, 196, 266, 348, 438];
  } else if (surface <= 40) {
    return [72, 112, 187, 257, 337, 427];
  } else if (surface <= 45) {
    return [69, 109, 179, 249, 329, 419];
  } else {
    return [70, 110, 180, 250, 330, 420];
  }
}

export function calculateProjectedGESLabel(
  currentEmissions: number,
  gesReduction: number,
  livingArea: number
): {
  projectedEmissions: number;
  projectedLabel: string;
} {
  const projectedEmissions = Math.max(0, currentEmissions - gesReduction / livingArea);

  let label = 'A';
  if (projectedEmissions > 6) label = 'B';
  if (projectedEmissions > 11) label = 'C';
  if (projectedEmissions > 30) label = 'D';
  if (projectedEmissions > 50) label = 'E';
  if (projectedEmissions > 70) label = 'F';
  if (projectedEmissions > 100) label = 'G';

  return {
    projectedEmissions: Math.round(projectedEmissions),
    projectedLabel: label,
  };
}
